import Coloris from '@melloware/coloris';
import '@melloware/coloris/dist/coloris.css';
import { useEffect, useRef } from 'react';

import { useControllableState } from '@/hook';
import { CustomEvent } from '@/util';

import type { VBaseInputProps } from '../VBaseInput';
import { VInput } from '../VInput';
import type { VInputProps } from '../VInput';
import { COLOR_PICKER_SIZE } from './constant';
import { useInitializeColorPicker } from './hook';
import twStyle from './style';
import type { VColorPickerProps } from './type';

Coloris.init();

const VColorPicker = ({
  disabled = false,
  fluid = false,
  hiddenValue = false,
  size = COLOR_PICKER_SIZE.SM,
  value = '#349af9',
  onChange,
  ...rest
}: VColorPickerProps &
  Omit<VInputProps<'text'>, 'type'> &
  VBaseInputProps &
  Omit<
    React.ComponentPropsWithRef<'input'>,
    'size' | 'type' | 'value' | 'onChange'
  >) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  const style = twStyle({ hiddenValue, size });
  const [_value, _setValue] = useControllableState({
    defaultProp: value,
    onChange: undefined,
    prop: value,
  });
  const handleChange = (newColor: string) => {
    if (!inputRef.current || newColor === valueRef.current) return;

    _setValue(newColor);
    onChangeRef.current?.(new CustomEvent('change', null, inputRef.current));
  };

  useInitializeColorPicker({
    inputRef,
    handleChange,
    style: style.clrField,
  });

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    const colorValue = document.querySelector(
      '#clr-color-value',
    ) as HTMLInputElement;

    if (!colorValue) return;

    const id = setTimeout(() => {
      colorValue.value = _value;
      colorValue.dispatchEvent(new Event('change'));
      inputRef.current?.dispatchEvent(new Event('input'));
    }, 16);

    return () => clearTimeout(id);
  }, [_value]);

  return (
    <VInput
      ref={inputRef}
      disabled={disabled}
      fluid={fluid}
      size={size}
      type="text"
      value={_value}
      {...rest}
    />
  );
};

export default VColorPicker;
